<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'petite-vue-i18n'
import { Listbox, ListboxButton, ListboxOptions } from '@headlessui/vue'
import { Session } from '../../types/session'
import { useClassStore, useSessionStore, useSignalRStore, useUserStore } from '../../stores'
import { UserRole } from '../../types/user'
import { ClassStatus } from '../../types/class'
import { utils } from '../../utils'

import ForceScreen from '../Control/ForceScreen.vue'

import LockIcon from '../../assets/icons/lock.svg'
import LinkIcon from '../../assets/icons/link.svg'
import CaretDownIcon from '../../assets/icons/caret-down.svg'
import IconVocab from '../../assets/icons/session-vocab.svg'
import IconPresent from '../../assets/icons/session-presentation.svg'
import IconPractice from '../../assets/icons/session-practice.svg'
import IconQuiz from '../../assets/icons/session-quiz.svg'
import IconEbook from '../../assets/icons/session-ebook.svg'
import IconSumm from '../../assets/icons/session-summary.svg'

const { t } = useI18n()
const signalr = useSignalRStore()
const sessionStore = useSessionStore()
const classStore = useClassStore()
const user = useUserStore()
let practiceCount = 0;

const props = defineProps<{ disabled?: boolean }>()

const sessionIconMap: Record<Session, any> = {
    [Session.vocab]: IconVocab,
    [Session.lecture]: IconPresent,
    [Session.practice]: IconPractice,
    [Session.practice2]: IconPractice,
    [Session.workbook]: IconEbook,
    [Session.quiz]: IconQuiz,
    [Session.summary]: IconSumm,
    [Session.custom]: IconEbook,
}

const activeSessions = computed(() =>
    Object.values(Session).filter((s) => sessionStore.classContent?.[s]?.is_active === 1)
)

function getSessionIcon(session: Session) {
    return sessionIconMap[session] || IconEbook
}

function getForceScreenIcon(session: Session) {
    if (!sessionStore.teacherSession) return
    if (sessionStore.teacherSession === session) return LinkIcon
    if (user.userRole === UserRole.student) return LockIcon
}

function formatSessionName(session: Session) {
    if (practiceCount === 0) {
        for (const item of sessionStore.rewardPointSessions) {
            if (item === Session.practice || item === Session.practice2) {
                practiceCount++;
            }
        }
    }
    const name = t(utils.getSessionName(session));
    if (session === Session.practice && practiceCount > 1) return `${name} 1`
    if (session === Session.practice2 && practiceCount > 1) return `${name} ${practiceCount}`
    return name
}

function changeCurrentSession(session: Session) {
    if (user.userRole === UserRole.student && sessionStore.teacherSession) return
    sessionStore.currentSession = session
    if (user.userRole === UserRole.teacher && sessionStore.teacherSession) {
        signalr.forceScreen()
    }
}
</script>

<template>
    <Listbox>
        <div class="relative min-w-max max-w-40">
            <ListboxButton
                class="relative flex gap-2 w-full cursor-pointer disabled:cursor-not-allowed rounded-md bg-orange-500 border-orange-700 shadow-[0_4px_0] shadow-orange-700 disabled:opacity-50 text-white py-2 pl-3 pr-10 text-left focus:outline-none sm:text-sm"
                :disabled="props.disabled">
                <component :is="getForceScreenIcon(sessionStore.currentSession)" class="size-6" />
                <component :is="getSessionIcon(sessionStore.currentSession)" v-if="getSessionIcon(sessionStore.currentSession)" class="size-6" />
                <span class="hidden lg:block truncate text-base">{{ formatSessionName(sessionStore.currentSession) }}</span>
                <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <CaretDownIcon class="size-5 text-white" aria-hidden="true" />
                </span>
            </ListboxButton>

            <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
                <ListboxOptions as="div" class="absolute mt-2 py-3 w-full min-w-max rounded-md bg-white flex flex-col gap-3 text-base shadow-neutral-200 shadow-[4px_4px_0] ring-1 ring-neutral-200 focus:outline-none sm:text-sm">
                    <div class="flex flex-col gap-3 max-h-72 overflow-y-auto scroll-smooth px-3">
                        <div v-for="session in activeSessions" :key="session" :class="[
                            'flex gap-2 items-center rounded-md py-3 px-5 bg-neutral-300 transition-all duration-150',
                            sessionStore.currentSession === session
                                ? 'bg-orange-600 text-white shadow-[2px_2px_0] shadow-orange-600/25 cursor-pointer'
                                : user.userRole === UserRole.student && sessionStore.teacherSession
                                    ? 'text-neutral-400 cursor-not-allowed'
                                    : 'text-neutral-400 cursor-pointer'
                        ]" @click="changeCurrentSession(session)">
                            <component :is="getForceScreenIcon(session)" class="size-5" />
                            <component v-if="getSessionIcon(session)" :is="getSessionIcon(session)" :class="[sessionStore.currentSession === session ? 'text-white' : 'text-neutral-400']" class="size-5" />
                            <span class="block truncate">{{ formatSessionName(session) }}</span>
                        </div>
                    </div>

                    <div v-if="user.userRole === UserRole.teacher && classStore.selectedClassDetails?.state === ClassStatus.on_going" class="px-3">
                        <ForceScreen class="shrink-0" />
                    </div>
                </ListboxOptions>
            </transition>
        </div>
    </Listbox>
</template>